import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { chevronBackOutline } from 'ionicons/icons'
import { useMemo, useState } from 'react'
import { useQuery } from 'urql'
import {
  type DateRange,
  DateRangePicker,
} from '../components/timeclock/DateRangePicker'
import { TimeActivitySummary } from '../components/timeclock/detail/TimeActivitySummary'
import { TimeSheetHistory } from '../components/timeclock/detail/TimeSheetHistory'
import { useRequiredTabNavigation } from '../context/TabNavigationContext'
import { GetTimeSheetQuery } from '../graphql/timeclock'
import {
  getWeekEnd,
  getWeekStart,
  minutesByTask,
  totalWorkedMinutes,
} from '../helpers/timesheet'
import { useFeature } from '../hooks/useFeatures'
import { useSwipeBack } from '../hooks/useSwipeBack'

export const TimeClockHistoryPage = () => {
  const { _ } = useLingui()
  // Client-side back — same reason as StandardLayout: IonBackButton's
  // defaultHref fallback did a hard document navigation (reloading the page).
  // Stays inside the Time Clock tab (see TabNavigationContext).
  const { goBack } = useRequiredTabNavigation()
  const handleBack = () => goBack('/timeclock')
  const swipeRef = useSwipeBack<HTMLIonContentElement>(handleBack)
  // Current week by default; each edge is editable independently (v1 parity)
  const [range, setRange] = useState<DateRange>(() => ({
    startTime: getWeekStart(new Date()),
    endTime: getWeekEnd(new Date()),
  }))
  const { startTime, endTime } = range

  // The route is always mounted (a gated route crashes the outlet); with the
  // module off TabShell's effect redirects, so fetch and render nothing here.
  const timeclocksEnabled = useFeature('timeclocksEnabled')
  const [{ data, fetching }] = useQuery({
    query: GetTimeSheetQuery,
    variables: { where: { startTime, endTime } },
    pause: !timeclocksEnabled,
  })

  const me = data?.me
  const timeSheet = me && 'timeSheet' in me ? me.timeSheet : null
  const shifts = timeSheet?.shifts ?? []

  const totalMinutes = useMemo(() => totalWorkedMinutes(shifts), [shifts])
  const hoursByTask = useMemo(() => minutesByTask(shifts), [shifts])

  if (!timeclocksEnabled) return null

  return (
    <IonPage>
      <IonHeader mode='ios'>
        <IonToolbar>
          <IonButtons slot='start'>
            <IonButton onClick={handleBack}>
              <IonIcon slot='start' icon={chevronBackOutline} />
              {_(msg`Back`)}
            </IonButton>
          </IonButtons>
          <IonTitle>{_(msg`Time Clock History`)}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent ref={swipeRef}>
        <DateRangePicker range={range} onRangeChange={setRange} />

        <TimeActivitySummary
          totalMinutes={totalMinutes}
          hoursByTask={hoursByTask}
          fetching={fetching}
        />

        <TimeSheetHistory shifts={shifts} fetching={fetching} />
      </IonContent>
    </IonPage>
  )
}
